// src/components/SiteSeeingListPage.js
import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  ListGroup,
  Form,
  Offcanvas,
  Button,
  Badge,
  Placeholder,
} from 'react-bootstrap';
import { Filter, Users, Calendar } from 'lucide-react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../FireBase';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import '../css/RoomListPage.css'; // reuse same css

const PRICE_RANGES = [
  { key: 'all',          label: 'All Prices' },
  { key: '0-10000',      label: 'Up to ₹10,000' },
  { key: '10000-20000',  label: '₹10,000–₹20,000' },
  { key: '20000+',       label: 'Above ₹20,000' },
];

const GUEST_OPTIONS = ['Any', '1', '2', '3', '4', '5+'];
const DAYS_OPTIONS  = ['Any', '1', '2', '3', '4', '5+'];

export default function SiteSeeingListPage() {
  const [packages, setPackages] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imgLoaded, setImgLoaded] = useState({});
  const [categories, setCategories] = useState([]);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState('All Packages');
  const [priceRange, setPriceRange] = useState('all');
  const [guestFilter, setGuestFilter] = useState('Any');
  const [daysFilter, setDaysFilter]   = useState('Any');

  const navigate = useNavigate();

  // Responsive listener
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fetch sightseeing packages
useEffect(() => {
  (async () => {
    try {
      const q = query(collection(db, 'sightseeing_packages'), orderBy('name'));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setPackages(data);
      console.log(data);

      // ✅ build categories from room/package names
      const allNames = data.map(p => p.name).filter(Boolean);
      setCategories(['All Packages', ...allNames]);
    } finally {
      setLoading(false);
    }
  })();
}, []);


  // Apply filters
  useEffect(() => {
    let tmp = [...packages];

        if (selectedCategory !== 'All Packages') {
        tmp = tmp.filter(p => p.name === selectedCategory);
        }


    if (priceRange !== 'all') {
      tmp = tmp.filter(p => {
        const price = Number(p.price) || 0;
        if (priceRange === '0-10000')     return price <= 10000;
        if (priceRange === '10000-20000') return price >= 10000 && price <= 20000;
        if (priceRange === '20000+')      return price >= 20000;
        return true;
      });
    }

    if (guestFilter !== 'Any') {
      if (guestFilter === '5+') tmp = tmp.filter(p => Number(p.guestCount || 0) >= 5);
      else                      tmp = tmp.filter(p => Number(p.guestCount || 0) === Number(guestFilter));
    }

    if (daysFilter !== 'Any') {
      if (daysFilter === '5+') tmp = tmp.filter(p => Number(p.days || 0) >= 5);
      else                     tmp = tmp.filter(p => Number(p.days || 0) === Number(daysFilter));
    }

    setFiltered(tmp);
  }, [packages, selectedCategory, priceRange, guestFilter, daysFilter]);

  // Sidebar filters
  const renderFilters = () => (
    <>
      <h5 className="mb-3">Categories</h5>
      <ListGroup variant="flush" className="mb-4">
        {categories.map(cat => (
          <ListGroup.Item
            key={cat}
            action
            active={selectedCategory === cat}
            variant={selectedCategory === cat ? 'primary' : 'light'}
            onClick={() => { setSelectedCategory(cat); setShowOffcanvas(false); }}
          >
            {cat}
          </ListGroup.Item>
        ))}
      </ListGroup>

      <h5 className="mb-2">Filter by Price</h5>
      <Form.Select
        value={priceRange}
        onChange={e => { setPriceRange(e.target.value); setShowOffcanvas(false); }}
      >
        {PRICE_RANGES.map(r => (
          <option key={r.key} value={r.key}>{r.label}</option>
        ))}
      </Form.Select>

      <Row className="g-2 mt-3">
        <Col xs={6}>
          <Form.Label className="mb-1">Guests</Form.Label>
          <Form.Select
            value={guestFilter}
            onChange={e => { setGuestFilter(e.target.value); setShowOffcanvas(false); }}
          >
            {GUEST_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Form.Select>
        </Col>
        <Col xs={6}>
          <Form.Label className="mb-1">Days</Form.Label>
          <Form.Select
            value={daysFilter}
            onChange={e => { setDaysFilter(e.target.value); setShowOffcanvas(false); }}
          >
            {DAYS_OPTIONS.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </Form.Select>
        </Col>
      </Row>
    </>
  );

  return (
    <Container fluid className="py-4" style={{ marginBottom: 60 }}>
      <Row>
        {/* Desktop Sidebar */}
        <Col md={3} className="d-none d-md-block" style={{ position: 'sticky', top: 20 }}>
          {renderFilters()}
        </Col>

        {/* Main */}
        <Col xs={12} md={9}>
          {loading ? (
            <Row className="g-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Col key={i} xs={12} md={6}>
                  <Card className="shadow-sm rounded overflow-hidden room-card">
                    <div
                      className="placeholder-glow"
                      style={{ height: 200, backgroundColor: '#e9ecef' }}
                    >
                      <Placeholder xs={12} animation="glow" />
                    </div>
                    <Card.Body>
                      <Placeholder animation="glow">
                        <Placeholder as={Card.Title} xs={8} />
                        <Placeholder xs={6} className="mt-2" />
                        {/* <Placeholder xs={4} className="mt-2" /> */}
                      </Placeholder>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <>
              {/* Mobile Floating Filter Button */}
              {isMobile && (
                <Button
                  className="d-md-none btn-own"
                  onClick={() => setShowOffcanvas(true)}
                  style={{
                    position: 'fixed',
                    bottom: 80,
                    right: 20,
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    zIndex: 1000,
                  }}
                >
                  <Filter size={24} />
                </Button>
              )}

              {/* Sightseeing Grid */}
              <Row className="g-4">
                {filtered.map(pkg => {
                  const cover = Array.isArray(pkg.images) && pkg.images.length > 0 ? pkg.images[0] : '';
                  const loaded = imgLoaded[pkg.id] === true;
                  const prices = pkg.vehiclePrices ? Object.values(pkg.vehiclePrices).map(Number) : [];
                const minPrice = prices.length > 0 ? Math.min(...prices) : null;


                  return (
                    <Col key={pkg.id} xs={12} md={6}>
                      <Card
                        className="shadow-sm rounded overflow-hidden room-card"
                        onClick={() => navigate(`/site-seeing-details/${pkg.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="position-relative">
                          {!loaded && (
                            <div
                              className="placeholder-glow"
                              style={{ height: 200, backgroundColor: '#e9ecef' }}
                            />
                          )}
                          {cover && (
                            <Card.Img
                              variant="top"
                              src={cover}
                              onLoad={() => setImgLoaded(prev => ({ ...prev, [pkg.id]: true }))}
                              style={{
                                height: 200,
                                objectFit: 'cover',
                                display: loaded ? 'block' : 'none',
                              }}
                              alt={pkg.name}
                            />
                          )}

                          {/* Example badge using first tag
                          {Array.isArray(pkg.tags) && pkg.tags.length > 0 && (
                            <Badge
                              bg="info"
                              pill
                              className="position-absolute"
                              style={{ top: 10, right: 10 }}
                            >
                              {pkg.tags[0]}
                            </Badge>
                          )} */}
                        </div>

                        <Card.Body>
                          {!loaded ? (
                            <>
                              <Skeleton as={Card.Title} width={200} />
                              <Skeleton width={120} className="mt-2" />
                              {/* <Skeleton width={140} className="mt-2" /> */}
                            </>
                          ) : (
                            <>
                              <div
                                className="d-flex justify-content-between align-items-center mb-1"
                                style={{ color: '#0f172a' }}
                              >
                                <b>{pkg.name}</b>
                                {/* <div className="d-flex align-items-center text-muted">
                                  <Users size={16} className="me-1" />
                                  {pkg.guestCount || 2} Guests
                                </div> */}
                              </div>
                             <div className="fw-bold mb-1" style={{ color: '#54b435' }}>
                                {minPrice !== null
                                    ? <>₹{minPrice.toLocaleString('en-IN')} <small className="text-muted ms-1">/ Onwards</small></>
                                    : <small className="text-muted">Price on request</small>
                                }
                                </div>

                              {/* <div className="text-muted d-flex align-items-center">
                                <Calendar size={16} className="me-1" />
                                {pkg.days || 1} Days
                              </div> */}
                            </>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}

                {filtered.length === 0 && !loading && (
                  <Col xs={12}>
                    <div className="text-center text-muted py-5">
                      No sightseeing packages match your filters.
                    </div>
                  </Col>
                )}
              </Row>
            </>
          )}
        </Col>
      </Row>

      {/* Mobile Offcanvas */}
      <Offcanvas show={showOffcanvas} onHide={() => setShowOffcanvas(false)} placement="end">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Filter Sightseeing</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>{renderFilters()}</Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
